import React, { useState, useEffect } from "react";
import {
  ThinTextField,
  ThinDropdownWithSearch,
} from "../common/GeneralComponent";
import { RadioGroup, FormControlLabel, Radio } from "@mui/material";
import { toast } from "react-toastify";
import { fetchData } from "../../utils/functions";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";

const AdministrativeCommunity = (props) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, schoolProfileId, onUpdate } = props;
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDqaApproved, setIsDqaApproved] = useState(false);
  const [isDqaRejected, setIsDqaRejected] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [loader, setLoader] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    smc_functional: "",
    smc_members: "",
    grant_received: "",
    grant_source: "",
    teaching_staff: "",
    non_teaching_staff: "",
    male_school_teachers: "",
    female_school_teachers: "",
  });

  const fetchSchoolAssessment = async () => {
    try {
      setLoader(true);
      setIsFetching(true);

      const response = await fetchData(
        `school-fetchById/${id}`,
        "GET",
        null,
        {},
        {},
        navigate
      );

      if (response?.data) {
        // setSchoolData(response.data);

        // Check for DQA approval at root level
        const isDqaApproved =
          response.data?.SchoolProfile.verified_from_dqa === 1 ||
          response.data?.SchoolProfile.verified_from_dqa === "1";

          const isDqaRejected = 
            response.data?.SchoolProfile.verified_from_dqa === 2 ||
            response.data?.SchoolProfile.verified_from_dqa === "2";

        setIsDqaApproved(isDqaApproved);
        setIsDqaRejected(isDqaRejected);
      }
    } catch (error) {
      console.error("Error fetching school data:", error);
      toast.error("Failed to load school data.");
    } finally {
      setLoader(false);
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchSchoolAssessment();
  }, [id]);

  // Yup schema for validation

  const validationSchema = Yup.object().shape({
    smc_functional: Yup.string().required(
      "SMC/PTA Functional status is required"
    ),

    smc_members: Yup.number()
      .transform((value, originalValue) =>
        String(originalValue).trim() === "" ? null : value
      )
      .nullable()
      .min(0, "Value cannot be negative")
      .when("smc_functional", {
        is: "Yes",
        then: (schema) =>
          schema.required(
            "Number of Members is required when SMC/PTA is Functional"
          ),
        otherwise: (schema) => schema.notRequired(),
      }),

    grant_received: Yup.string().required("Grant Received status is required"),

    grant_source: Yup.string()
      .nullable()
      .when("grant_received", {
        is: "Yes",
        then: (schema) => schema.required("Source is required "),
        otherwise: (schema) => schema.notRequired(),
      }),

    teaching_staff: Yup.number()
      .transform((value, originalValue) =>
        String(originalValue).trim() === "" ? null : value
      )
      .typeError("Teaching Staff must be a number")
      .min(0, "Value cannot be negative")
      .required("Teaching Staff is required"),

    non_teaching_staff: Yup.number()
      .transform((value, originalValue) =>
        String(originalValue).trim() === "" ? null : value
      )
      .typeError("Non-Teaching Staff must be a number")
      .min(0, "Value cannot be negative")
      .required("Non-Teaching Staff is required"),
    male_school_teachers: Yup.number()
      .transform((value, originalValue) =>
        String(originalValue).trim() === "" ? null : value
      )
      .typeError("Male Teachers must be a number")
      .min(0, "Value cannot be negative")
      .required("Male Teachers is required"),

    female_school_teachers: Yup.number()
      .transform((value, originalValue) =>
        String(originalValue).trim() === "" ? null : value
      )
      .typeError("Female Teachers must be a number")
      .min(0, "Value cannot be negative")
      .required("Female Teachers is required"),
  });

  const booleanToYesNo = (value) => {
    if (value === true) return "Yes";
    if (value === false) return "No";
    return "";
  };

  // 🔹 Load API data into form when data prop changes
  useEffect(() => {
    if (data) {
      setFormData({
        smc_functional: booleanToYesNo(data.smc_pta_functional),
        smc_members: String(data.smc_pta_members || ""),
        grant_received: booleanToYesNo(data.grant_received),
        grant_source: data.grant_source || "",
        teaching_staff: String(data.teaching_staff || ""),
        non_teaching_staff: String(data.non_teaching_staff ?? ""),
        male_school_teachers: String(data.male_school_teachers ?? ""),
        female_school_teachers: String(data.female_school_teachers ?? ""),
      });
    }
  }, [data]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when field is updated
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleNumericChange = (field, value) => {
    if (value === "" || /^\d+$/.test(value)) {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      // Clear error when field is updated
      if (errors[field]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    }
  };

  const handleUpdate = async () => {
    setIsUpdating(true);
    setErrors({});

    try {
      await validationSchema.validate(formData, { abortEarly: false });

      const profileId = schoolProfileId || data?.school_profile_id;

      if (!profileId) {
        toast.error("School profile ID not found");
        return;
      }

      // Prepare the data for API
      const updatePayload = {
        smc_pta_functional: formData.smc_functional === "Yes",
        smc_pta_members: parseInt(formData.smc_members) || 0,
        grant_received: formData.grant_received === "Yes",
        grant_source:
          formData.grant_received === "Yes" ? formData.grant_source : null,
        teaching_staff: parseInt(formData.teaching_staff) || 0,
        non_teaching_staff: parseInt(formData.non_teaching_staff) || 0,
        male_school_teachers: parseInt(formData.male_school_teachers) || 0,
        female_school_teachers: parseInt(formData.female_school_teachers) || 0,
      };

      // Call the combined update API
      const response = await fetchData(
        `school-combined-update/${profileId}`,
        "PUT",
        {
          school_administrative_details: JSON.stringify(updatePayload),
          school_profile: "{}",
          school_infrastructure: "{}",
          school_learning_facilities: "{}",
          school_additional_notes: "{}",
        },
        { "Content-Type": "application/json" }
      );

      if (response.success) {
        toast.success("Administrative data updated successfully!");
        if (onUpdate) {
          onUpdate(); // Refresh parent component if needed
        }
      } else {
        toast.error(response.message || "Failed to update administrative data");
      }
    } catch (error) {
      if (error.inner) {
        const newErrors = {};
        error.inner.forEach((err) => {
          newErrors[err.path] = err.message;
        });
        setErrors(newErrors);
        // toast.error("Please fix the validation errors");
      } else {
        toast.error("Error updating administrative data");
        console.error("Update error:", error);
      }
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        {isDqaApproved && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Administrative & Community Engagement
            </h2>
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
              Approved from DQA
            </span>
          </>
        )}
        {isDqaRejected && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Administrative & Community Engagement
            </h2>
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
              Rejected from DQA
            </span>
          </>
        )}
      </div>
      {/* <div className="flex justify-between items-center mb-6"></div> */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* SMC/PTA Functional */}
        <div>
          <label className="block font-medium mb-1">SMC/PTA Functional *</label>
          <RadioGroup
            row
            value={formData.smc_functional}
            onChange={(e) => handleChange("smc_functional", e.target.value)}
          >
            <FormControlLabel disabled={isDqaApproved || isDqaRejected} value="Yes" control={<Radio disabled={isDqaApproved || isDqaRejected} />} label="Yes" />
            <FormControlLabel disabled={isDqaApproved || isDqaRejected} value="No" control={<Radio disabled={isDqaApproved || isDqaRejected} />} label="No" />
          </RadioGroup>
          {errors.smc_functional && (
            <p className="text-red-500 text-xs">{errors.smc_functional}</p>
          )}
          {formData.smc_functional === "Yes" && (
            <div className="mt-2">
              <ThinTextField disabled={isDqaApproved || isDqaRejected}
                label="No. of Members "
                value={formData.smc_members}
                onChange={(e) =>
                  handleNumericChange("smc_members", e.target.value)
                }
              />
              {errors.smc_members && (
                <p className="text-red-500 text-xs">{errors.smc_members}</p>
              )}
            </div>
          )}
        </div>

        {/* Grant Received */}
        <div>
          <label className="block font-medium mb-1">Grant Received *</label>
          <RadioGroup
            row
            value={formData.grant_received}
            onChange={(e) => handleChange("grant_received", e.target.value)}
          >
            <FormControlLabel disabled={isDqaApproved || isDqaRejected} value="Yes" control={<Radio disabled={isDqaApproved || isDqaRejected} />} label="Yes" />
            <FormControlLabel disabled={isDqaApproved || isDqaRejected} value="No" control={<Radio disabled={isDqaApproved || isDqaRejected} />} label="No" />
          </RadioGroup>
          {errors.grant_received && (
            <p className="text-red-500 text-xs">{errors.grant_received}</p>
          )}
          {/* {formData.grant_received === "Yes" && (
            <div className="mt-2">
              <ThinTextField
                label="Source (Govt / NGO) "
                value={formData.grant_source}
                onChange={(e) => handleChange("grant_source", e.target.value)}
              />
              {errors.grant_source && <p className="text-red-500 text-xs">{errors.grant_source}</p>}
            </div>
          )} */}
          {formData.grant_received === "Yes" && (
            <div className="mt-2">
              <ThinDropdownWithSearch disabled={isDqaApproved || isDqaRejected}
                label="Source (Govt / NGO)"
                value={formData.grant_source}
                onChange={(value) => handleChange("grant_source", value)}
                options={[
                  { value: "Govt", label: "Govt" },
                  { value: "NGO", label: "NGO" },
                ]}
              />
              {errors.grant_source && (
                <p className="text-red-500 text-xs">{errors.grant_source}</p>
              )}
            </div>
          )}
        </div>

        {/* Staff Counts */}
        <div className="col-span-2">
          {/* Row 1: Teaching & Non-Teaching side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <ThinTextField disabled={isDqaApproved || isDqaRejected}
                label="Total Teaching Staff "
                type="number"
                value={formData.teaching_staff}
                onChange={(e) =>
                  handleNumericChange("teaching_staff", e.target.value)
                }
              />
              {errors.teaching_staff && (
                <p className="text-red-500 text-xs">{errors.teaching_staff}</p>
              )}
            </div>

            <div>
              <ThinTextField disabled={isDqaApproved || isDqaRejected}
                label="Total Non-Teaching Staff "
                type="number"
                value={formData.non_teaching_staff}
                onChange={(e) =>
                  handleNumericChange("non_teaching_staff", e.target.value)
                }
              />
              {errors.non_teaching_staff && (
                <p className="text-red-500 text-xs">
                  {errors.non_teaching_staff}
                </p>
              )}
            </div>
          </div>

          {/* Row 2: Male & Female Teachers side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <ThinTextField disabled={isDqaApproved || isDqaRejected}
                label="Male Teachers"
                type="number"
                value={formData.male_school_teachers}
                onChange={(e) =>
                  handleNumericChange("male_school_teachers", e.target.value)
                }
              />
              {errors.male_school_teachers && (
                <p className="text-red-500 text-xs">
                  {errors.male_school_teachers}
                </p>
              )}
            </div>

            <div>
              <ThinTextField disabled={isDqaApproved || isDqaRejected}
                label="Female Teachers"
                type="number"
                value={formData.female_school_teachers}
                onChange={(e) =>
                  handleNumericChange("female_school_teachers", e.target.value)
                }
              />
              {errors.female_school_teachers && (
                <p className="text-red-500 text-xs">
                  {errors.female_school_teachers}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      {!isDqaApproved && !isDqaRejected ? (
        <div className="mt-8 flex justify-end">
          <button
            type="button"
            onClick={handleUpdate}
            disabled={isUpdating}
            className={`${
              isUpdating
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-teal-600 hover:bg-teal-700"
            } text-white px-4 py-2 rounded-lg transition duration-200 font-medium`}
          >
            {isUpdating ? "Updating..." : "Update"}
          </button>
        </div>
      ) : (
        <div className=""></div>
      )}
    </>
  );
};

export default AdministrativeCommunity;
